import React, { Fragment } from 'react';
import { injectIntl } from 'react-intl';
import { Grid, Typography } from '@mui/material';
import _ from 'lodash';
import AdviserInfoContactEntry from '../AdviserInfoContactEntry/AdviserInfoContactEntry';
import { formatPhoneNumber } from '../../../utils/user';
import { formatOpenUrl } from '../../../utils/url';
import AdviserInfoCardAbstract from '../AdviserInfoCardAbstract/AdviserInfoCardAbstract';
import { Capacitor } from '@capacitor/core';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import NativeOrWeb from '../../../utils/native';
import { tracking } from '../../../utils/tracking';
import { buildProfilePlusTravelKitHref } from './travelKitHref';

const FullWidthStyle = {
  width: '100%'
};

const PoweredByTypoStyle = {
  fontSize: '80%'
};

// Same underlined-text treatment as the contact entries, without their icon — the 🌏 title already
// marks the row. Darker than their #9e9e9e (about 2.7:1 on the white card): a link has to be legible
// outdoors, and #616161 clears the 4.5:1 AA minimum.
const TravelKitLinkStyle = {
  color: '#616161',
  textDecoration: 'underline',
  cursor: 'pointer',
  // One line: the copy is kept short enough to fit the card, and the ellipsis only catches a
  // narrow screen or a long translation rather than letting the link wrap onto a second line.
  display: 'inline-block',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  verticalAlign: 'top'
};

const AdviserInfoPageContact = props => {
  const { intl, adviserInfo, isIntoView, isPreview = false } = props;

  const {
    phoneNumber,
    phoneNumber2,
    whatsapp,
    workEmail,
    wechatId,
    lineId,
    facebookLink,
    instagram,
    realLinkedinLink,
    linkedinLink
  } = adviserInfo || {};

  const goPhoneNumber = phoneNumber
    ? () => {
        window.open(`tel:${phoneNumber}`, '_blank');
      }
    : undefined;
  const goPhoneNumber2 = phoneNumber2
    ? () => {
        window.open(`tel:${phoneNumber2}`, '_blank');
      }
    : undefined;
  const goWhatsapp = whatsapp
    ? () => {
        window.open(`https://api.whatsapp.com/send?phone=${whatsapp.replace('+', '')}`, '_blank');
      }
    : undefined;
  const goWorkEmail = workEmail
    ? () => {
        window.open(`mailto:${workEmail}`, '_blank');
      }
    : undefined;
  const goFacebook = () => {
    window.open(formatOpenUrl(facebookLink), '_blank');
  };

  const goInstagram = () => {
    window.open(`https://www.instagram.com/${instagram}`, '_blank');
  };

  const goLinkedin = () => {
    window.open(formatOpenUrl(realLinkedinLink), '_blank');
  };

  const goMyWebpage = () => {
    window.open(formatOpenUrl(linkedinLink), '_blank');
  };

  // Toast only when the copy actually landed — see the note on `copyToClipboard` for what the
  // web path can and cannot promise. The id stays readable on the card either way.
  const copyThenToast = async (text, messageId) => {
    let copied = false;
    try {
      copied = await NativeOrWeb.copyToClipboard(text);
    } catch (error) {
      // leave copied false
    }

    if (copied) {
      toast.info(IconToast('info', intl.formatMessage({ id: messageId })), { className: 'new-toast' });
    }
  };

  const onClickWechat = wechatId ? () => copyThenToast(wechatId, 'wechat-msg-copied') : undefined;

  const onClickLine = lineId ? () => copyThenToast(lineId, 'line-msg-copied') : undefined;

  // The adviser's own travel kit page, in the e-card's current language and theme colour.
  const travelKitHref = adviserInfo?._id
    ? buildProfilePlusTravelKitHref({
        adviserId: adviserInfo._id,
        locale: intl.locale,
        themeColor: adviserInfo.themeColor
      })
    : undefined;

  const onClickTravelKit = event => {
    // Every other outbound link on this card goes through window.open, which the native shell
    // already handles. A bare target="_blank" anchor depends on the WebView's multi-window support,
    // so on the Capacitor apps take over the click; the web keeps the anchor's default behaviour.
    if (Capacitor.isNativePlatform()) {
      event.preventDefault();
      window.open(travelKitHref, '_blank');
    }
    tracking('User Click on TravelKit Link - My Adviser Profile+', { adviserId: adviserInfo._id, isPreview });
  };

  return (
    <AdviserInfoCardAbstract
      className="adviser-info-page-contact"
      title={intl.formatMessage({ id: 'adv-info-contact-title' })}
      adviserInfo={adviserInfo}
      contentHeight="calc(100% - 110px)"
      isIntoView={isIntoView}
    >
      <Grid container wrap="nowrap" sx={{ flexDirection: 'column' }}>
        <Grid style={{ ...FullWidthStyle, flexGrow: 1, paddingBottom: 34 }} className="content-container">
          <Grid container spacing={3} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Grid style={{ ...FullWidthStyle, flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Grid style={FullWidthStyle}>
                  <AdviserInfoContactEntry
                    imgSrc="img/phone.svg"
                    alt="phone"
                    text={formatPhoneNumber(phoneNumber) || '-'}
                    textStyle={phoneNumber ? { textDecoration: 'underline' } : undefined}
                    onClick={goPhoneNumber}
                  />
                </Grid>

                {phoneNumber2 && (
                  <Grid style={FullWidthStyle}>
                    <AdviserInfoContactEntry
                      imgSrc="img/phone.svg"
                      alt="phone"
                      text={formatPhoneNumber(phoneNumber2) || '-'}
                      textStyle={phoneNumber2 ? { textDecoration: 'underline' } : undefined}
                      onClick={goPhoneNumber2}
                    />
                  </Grid>
                )}

                <Grid style={FullWidthStyle}>
                  <AdviserInfoContactEntry
                    imgSrc="img/whatsapp.svg"
                    alt="whatsapp"
                    text={formatPhoneNumber(whatsapp) || '-'}
                    textStyle={whatsapp ? { textDecoration: 'underline' } : undefined}
                    onClick={goWhatsapp}
                  />
                </Grid>

                <Grid style={FullWidthStyle}>
                  <AdviserInfoContactEntry
                    imgSrc="img/email.svg"
                    alt="email"
                    text={workEmail || '-'}
                    textStyle={workEmail ? { textDecoration: 'underline' } : undefined}
                    onClick={goWorkEmail}
                  />
                </Grid>

                <Grid style={FullWidthStyle}>
                  <AdviserInfoContactEntry
                    imgSrc="img/wechat.svg"
                    alt="wechat"
                    text={wechatId || '-'}
                    textStyle={wechatId ? { textDecoration: 'underline' } : undefined}
                    type="wechat"
                    adviserInfo={adviserInfo}
                    onClick={onClickWechat}
                  />
                </Grid>

                <Grid style={FullWidthStyle}>
                  <AdviserInfoContactEntry
                    imgSrc="img/line.svg"
                    alt="line"
                    text={lineId || '-'}
                    textStyle={lineId ? { textDecoration: 'underline' } : undefined}
                    onClick={onClickLine}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid style={FullWidthStyle}>
              <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Grid>
                  <Typography
                    className="emoji"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    📱
                  </Typography>
                  <Typography
                    className="title"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    {intl.formatMessage({ id: 'social-platform' })}
                  </Typography>
                </Grid>
                {facebookLink || instagram || realLinkedinLink || linkedinLink ? (
                  <Fragment>
                    {facebookLink && (
                      <Grid style={FullWidthStyle}>
                        <AdviserInfoContactEntry
                          imgSrc="img/facebook.svg"
                          alt="facebook"
                          text="Facebook"
                          textStyle={{ textDecoration: 'underline' }}
                          onClick={goFacebook}
                        />
                      </Grid>
                    )}
                    {instagram && (
                      <Grid style={FullWidthStyle}>
                        <AdviserInfoContactEntry
                          imgSrc="img/instagram.svg"
                          alt="instagram"
                          text={`IG: ${instagram}`}
                          textStyle={{ textDecoration: 'underline' }}
                          onClick={goInstagram}
                        />
                      </Grid>
                    )}
                    {realLinkedinLink && (
                      <Grid style={FullWidthStyle}>
                        <AdviserInfoContactEntry
                          imgSrc="img/linkedin.svg"
                          alt="linkedin"
                          text="Linkedin"
                          textStyle={{ textDecoration: 'underline' }}
                          onClick={goLinkedin}
                        />
                      </Grid>
                    )}
                    {linkedinLink && (
                      <Grid style={FullWidthStyle}>
                        <AdviserInfoContactEntry
                          imgSrc="img/web.svg"
                          alt="my-webpage"
                          text={linkedinLink}
                          textStyle={{ textDecoration: 'underline' }}
                          onClick={goMyWebpage}
                        />
                      </Grid>
                    )}
                  </Fragment>
                ) : (
                  <Typography>-</Typography>
                )}
              </Grid>
            </Grid>

            {travelKitHref && (
              <Grid style={FullWidthStyle}>
                <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Grid>
                    <Typography
                      className="emoji"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      🌏
                    </Typography>
                    <Typography
                      className="title"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      {intl.formatMessage({ id: 'adv-info-travel-kit-title' })}
                    </Typography>
                  </Grid>
                  <Grid style={FullWidthStyle}>
                    <Typography
                      component="a"
                      href={travelKitHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={TravelKitLinkStyle}
                      onClick={onClickTravelKit}
                    >
                      {intl.formatMessage({ id: 'adv-info-travel-kit-link' })}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid style={FullWidthStyle}>
              <Grid
                container
                spacing={1}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'flex-end'
                }}
              >
                <Grid>
                  <Typography style={PoweredByTypoStyle}>Powered By</Typography>
                </Grid>
                <Grid>
                  <img src="img/logo.png" alt="portfoplus" height={25} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AdviserInfoCardAbstract>
  );
};

export default injectIntl(AdviserInfoPageContact);
